'use client';

import { createClient } from '@/lib/supabase/client';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const newsService = {
  async getAll(): Promise<NewsItem[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('news_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        excerpt: row.excerpt,
        content: row.content,
        category: row.category,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get news items error:', error.message);
      throw error;
    }
  },

  async getPublished(): Promise<NewsItem[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('news_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        excerpt: row.excerpt,
        content: row.content,
        category: row.category,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get published news items error:', error.message);
      return [];
    }
  },

  async create(news: Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<NewsItem | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('news_items')
        .insert({
          title: news.title,
          excerpt: news.excerpt,
          content: news.content,
          category: news.category,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        title: data.title,
        excerpt: data.excerpt,
        content: data.content,
        category: data.category,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create news item error:', error.message);
      throw error;
    }
  },

  async update(id: string, news: Partial<Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>>): Promise<NewsItem | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (news.title !== undefined) updateData.title = news.title;
      if (news.excerpt !== undefined) updateData.excerpt = news.excerpt;
      if (news.content !== undefined) updateData.content = news.content;
      if (news.category !== undefined) updateData.category = news.category;

      const { data, error } = await supabase
        .from('news_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        title: data.title,
        excerpt: data.excerpt,
        content: data.content,
        category: data.category,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update news item error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('news_items')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.error('Delete news item error:', error.message);
      throw error;
    }
  },
};